import React, { useState, useEffect } from "react";
import { CircularProgress, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { Effect } from "effector";
import { Vehicle, NewVehicle, EditVehicle } from "../../../../types/vehicles.types";
import { Customer } from "../../../../types/customers.types";
import { Fleet } from "../../../../types/fleets.types";
import { useUnit } from "effector-react";
import { getVehiclePlateData, editVehicle } from "../../../../services/vehicleService";
import { CustomersStore } from "../../../../stores/CustomersStore/CustomersStore";
import { FleetsStore } from "../../../../stores/FleetsStore/FleetsStore";
import SimpleModal from "../../../../components/SimpleModal/SimpleModal";
import PlateField, { ERROR_TYPE } from "../../../Fleets/components/PlateField/PlateField";
import SelectClient from "../../../Fleets/components/SelectClient/SelectClient";
import SelectFleet from "../SelectFleet/SelectFleet";
import { Container, Row } from "./VehicleFormModalStyles";

interface Props {
  vehicle?: Vehicle;
  effect: Effect<any, any>;
  title: string;
  confirmText: string;
  cancelText: string;
  TriggerComponent: React.ReactElement<any>;
  isEdit?: boolean;
}

const VehicleFormModal: React.FC<Props> = ({
  vehicle,
  effect,
  title,
  confirmText,
  cancelText,
  TriggerComponent,
  isEdit = false,
}) => {
  const { customers } = useUnit(CustomersStore);
  const { fleets } = useUnit(FleetsStore);

  const [open, setOpen] = useState(false);
  const [licencePlate, setLicencePlate] = useState(vehicle?.licencePlate ?? "");
  const [client, setClient] = useState<Customer>({} as Customer);
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (vehicle && open) {
      setLicencePlate(vehicle.licencePlate);
      setClient(customers.find((c) => c.id === vehicle.clientId) ?? ({} as Customer));
      setFleet(fleets.find((f) => f.id === vehicle.fleetId) ?? null);
    }
  }, [vehicle, open, customers, fleets]);

  useEffect(() => {
    if (!open) return;

    const unsubscribeDone = effect.doneData.watch(() => {
      toast.success(isEdit ? "Veículo editado com sucesso!" : "Veículo cadastrado com sucesso!");
      if (!isEdit) handleClear();
      setOpen(false);
      setLoading(false);
    });

    const unsubscribeFail = effect.fail.watch(({ error }: any) => {
      toast.error(error?.message ?? (isEdit ? "Erro ao editar veículo!" : "Erro ao cadastrar veículo!"));
      setLoading(false);
    });

    return () => {
      unsubscribeDone();
      unsubscribeFail();
    };
  }, [effect, isEdit, open]);

  const handleClear = () => {
    setLicencePlate("");
    setClient({} as Customer);
    setFleet(null);
  };

  const handleSubmit = async () => {
    const rawPlate = licencePlate.replace(/[^A-Z0-9]/gi, "").toUpperCase();

    if (!rawPlate || !client?.id || !fleet?.id) {
      toast.error("Preencha a placa, o cliente e a frota!");
      return;
    }

    setLoading(true);

    if (isEdit && vehicle) {
      const payload: EditVehicle = {
        id: vehicle.id,
        licencePlate: vehicle.licencePlate,
        clientId: client.id,
        fleetId: fleet?.id ?? null,
        userId: vehicle.userId || "Desconhecido",
        brand: vehicle.brand ?? null,
        model: vehicle.model ?? null,
        subModel: vehicle.subModel ?? null,
        version: vehicle.version ?? null,
        year: vehicle.year ?? null,
        color: vehicle.color ?? null,
        cylinderCapacity: vehicle.cylinderCapacity ?? null,
        fuelType: vehicle.fuelType ?? null,
        bodyType: vehicle.bodyType ?? null,
        vehicleType: vehicle.vehicleType ?? null,
        city: vehicle.city ?? null,
        uf: vehicle.uf ?? null,
        fipeId: vehicle.fipeId ?? null,
        fipeBrand: vehicle.fipeBrand ?? null,
        fipeModel: vehicle.fipeModel ?? null,
        fipeValue: vehicle.fipeValue ?? null,
        fipeRawValue: vehicle.fipeRawValue ?? null,
        rawJson: vehicle.rawJson ?? null,
      };
      effect(payload);
      return;
    }

    // ADD: busca dados da placa
    try {
      const plateData = await getVehiclePlateData(rawPlate);
      const { id, user, clientId: existingClientId, fleetId: existingFleetId, userId: existingUserId, createdAt: _c, updatedAt: _u, ...plateFields } = plateData;

      // Veículo já vinculado a frota ou cliente
      if (existingClientId || existingFleetId) {
        toast.error("Veiculo já cadastrado em uma frota ou cliente.");
        setLoading(false);
        return;
      }

      // Veículo existe mas não tem frota/cliente — atualiza via PUT
      if (id) {
        const payload: EditVehicle = {
          id,
          ...plateFields,
          licencePlate: rawPlate,
          clientId: client.id,
          fleetId: fleet?.id ?? null,
          userId: existingUserId || "Desconhecido",
        };
        await editVehicle(payload);
        toast.success("Veículo cadastrado com sucesso!");
        handleClear();
        setOpen(false);
        setLoading(false);
        return;
      }

      // Veículo não existe ainda — cadastro normal via POST
      const payload: NewVehicle = {
        ...plateFields,
        licencePlate: rawPlate,
        clientId: client.id,
        fleetId: fleet?.id ?? null,
        userId: "Desconhecido",
      };
      effect(payload);
    } catch (error: any) {
      toast.error(error?.message ?? "Erro ao consultar placa.");
      setLoading(false);
    }
  };

  const modalBody = (
    <Container>
      {isEdit ? (
        <TextField
          fullWidth
          label="Placa"
          variant="outlined"
          value={licencePlate}
          disabled
        />
      ) : (
        <PlateField
          value={licencePlate}
          onChange={setLicencePlate}
          errorType={ERROR_TYPE.BOTH}
          fullWidth
        />
      )}
      <Row>
        <div style={{ flex: 1 }}>
          <SelectClient client={client} onChange={setClient} />
        </div>
        <div style={{ flex: 1 }}>
          <SelectFleet clientId={client?.id ?? ""} fleet={fleet} onChange={setFleet} />
        </div>
      </Row>
      {loading && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress size={24} />
        </div>
      )}
    </Container>
  );

  return (
    <SimpleModal
      open={open}
      setOpen={setOpen}
      Component={TriggerComponent}
      onClose={!isEdit ? handleClear : undefined}
      onConfirm={handleSubmit}
      title={title}
      confirmText={loading ? "Aguarde..." : confirmText}
      cancelText={cancelText}
      body={modalBody}
      maxWidth="sm"
    />
  );
};

export default VehicleFormModal;
